/**
 * SDUI_DEFAULTS — Centralized default configs for all SDUI surfaces
 *
 * IMPORTANT: These MUST mirror the seed data in the sdui_surface_configs table.
 * When the DB seed changes, update these defaults too.
 *
 * Used by useSDUIConfig to return values synchronously on first render
 * while fetching server config in the background.
 *
 * Task: S2 #1158
 * Agent: Diego <diego@liftping.net>
 */

// ============================================================================
// Types
// ============================================================================

/** Blur teaser surface config */
export interface SDUIBlurConfig {
	blurAmountLight: number;
	blurAmountHeavy: number;
	teaserCopy: string | null;
	ctaCopy: string | null;
	patternCountThreshold: number | null;
	issueCountThreshold: number | null;
}

/** Score delta surface config */
export interface SDUIScoreDeltaConfig {
	displayFormat: "delta" | "arrow" | "percentage";
	showSign: boolean;
	showArrow: boolean;
}

/** Consent modal / trial prompt surface config */
export interface SDUIConsentModalConfig {
	/** Whether the consent modal system is enabled */
	enabled: boolean;
	/** Number of analyses before showing the first prompt */
	analysisThreshold: number;
	/** Percentage past threshold for inline banner prompt (default: 10 = 10%) */
	inlineBannerThreshold: number;
	/** Percentage past threshold for blur overlay prompt (default: 50 = 50%) */
	blurOverlayThreshold: number;
	/** Percentage past threshold for bottom sheet prompt (default: 80 = 80%) */
	bottomSheetThreshold: number;
	/** Number of analyses to wait after a dismiss before showing again */
	dismissCooldown: number;
	/** Whether the modal can be dismissed (false = forced upgrade) */
	dismissable: boolean;
}

/** Referral share surface config */
export interface SDUIReferralShareConfig {
	/** Override for share text footer (default: "Shared from DramaDetector") */
	shareFooter: string | null;
	/** Override for share CTA copy */
	shareCtaCopy: string | null;
}

/** Subscription page surface config */
export interface SDUISubscriptionPageConfig {
	/** Show DramaX plan options on subscription screen */
	showDramaxPlans: boolean;
	/** Show family plan options on subscription screen */
	showFamilyPlans: boolean;
	/** Enable checkout flow (disable to show "coming soon" instead) */
	checkoutEnabled: boolean;
}

/** OG image generation surface config */
export interface SDUIOGImageConfig {
	/** Show drama score on OG image */
	showScore: boolean;
	/** Show summary text on OG image */
	showSummary: boolean;
	/** Max summary length on OG image */
	maxSummaryLength: number;
	/** Override for footer text */
	footerText: string | null;
}

export type SDUIShareModalFormatId =
	| "portrait"
	| "square"
	| "reel"
	| "horizontal";

export interface SDUIShareModalFormatOption {
	id: SDUIShareModalFormatId;
	label: string;
	sublabel: string;
	hint: string;
	pw: number;
	ph: number;
}

/** Share modal surface config */
export interface SDUIShareModalConfig {
	/** Available formats in display order */
	availableFormats: SDUIShareModalFormatOption[];
	/** Default format on modal open */
	defaultFormat: SDUIShareModalFormatId;
	/** Share modal personality tone */
	shareModalTone: "playful" | "clinical" | "editorial";
	/** Template ordering */
	templateOrdering: "narrator-first" | "expert-first";
	/** Show format hint text after selection */
	showFormatHint: boolean;
	/** Show live preview thumbnail */
	showPreview: boolean;
	/** Footer co-branding text */
	shareFooter: string | null;
}

/** Habit Engine surface config (Track H Phase 2) */
export interface SDUIHabitEngineConfig {
	/** Number of consecutive days before streak reward triggers */
	streakRewardThreshold: number;
	/** Number of analyses required to unlock quests */
	questUnlockAnalysisCount: number;
	/** Type of celebration animation on achievement */
	celebrationType: "confetti" | "badge" | "both";
	/** Hours of grace before a streak is reset */
	streakResetGracePeriodHours: number;
	/** Number of daily quests shown */
	dailyQuestCount: number;
}

/** DramaCards surface config (Track H Phase 2) */
export interface SDUIDramaCardsConfig {
	/** Number of cards shown per session */
	cardsPerSession: number;
	/** Score threshold to unlock a new deck */
	deckUnlockThreshold: number;
	/** Whether to show difficulty badge on cards */
	showDifficultyBadge: boolean;
	/** Cooldown minutes between sessions */
	sessionCooldownMinutes: number;
	/** Max daily sessions for free tier */
	maxDailySessionsFree: number;
	/** Max daily sessions for premium tier */
	maxDailySessionsPremium: number;
}

/** Union type for all surface configs */
export type SDUISurfaceConfig =
	| SDUIBlurConfig
	| SDUIScoreDeltaConfig
	| SDUIConsentModalConfig
	| SDUIReferralShareConfig
	| SDUISubscriptionPageConfig
	| SDUIOGImageConfig
	| SDUIShareModalConfig
	| SDUIHabitEngineConfig
	| SDUIDramaCardsConfig;

/**
 * Typed surface-key → config-type map.
 * Provides compile-time enforcement: useSDUIConfig('blur-teaser') returns SDUIBlurConfig.
 */
export interface SDUISurfaceMap {
	"blur-teaser": SDUIBlurConfig;
	"score-delta": SDUIScoreDeltaConfig;
	"consent-modal": SDUIConsentModalConfig;
	"referral-share": SDUIReferralShareConfig;
	"subscription-page": SDUISubscriptionPageConfig;
	"og-image": SDUIOGImageConfig;
	"share-modal-config": SDUIShareModalConfig;
	"habit-engine": SDUIHabitEngineConfig;
	dramacards: SDUIDramaCardsConfig;
}

// ============================================================================
// Defaults — mirrors DB seed data exactly
// ============================================================================

export const SDUI_DEFAULTS: { [K in keyof SDUISurfaceMap]: SDUISurfaceMap[K] } =
	{
		"blur-teaser": {
			blurAmountLight: 6,
			blurAmountHeavy: 12,
			teaserCopy: null,
			ctaCopy: null,
			patternCountThreshold: null,
			issueCountThreshold: null,
		},
		"score-delta": {
			displayFormat: "delta",
			showSign: true,
			showArrow: false,
		},
		"consent-modal": {
			enabled: false,
			analysisThreshold: 30,
			inlineBannerThreshold: 10,
			blurOverlayThreshold: 50,
			bottomSheetThreshold: 80,
			dismissCooldown: 5,
			dismissable: true,
		},
		"referral-share": {
			shareFooter: null,
			shareCtaCopy: null,
		},
		"subscription-page": {
			showDramaxPlans: false,
			showFamilyPlans: false,
			checkoutEnabled: true,
		},
		"og-image": {
			showScore: true,
			showSummary: true,
			maxSummaryLength: 160,
			footerText: null,
		},
		"share-modal-config": {
			availableFormats: [
				{
					id: "portrait",
					label: "Your Episode",
					sublabel: "1080×1350 · 4:5",
					hint: "Best for Instagram feed and Facebook posts",
					pw: 30,
					ph: 37.5,
				},
				{
					id: "square",
					label: "Your Score",
					sublabel: "1080×1080 · 1:1",
					hint: "Good for grids and multi-platform reposting",
					pw: 32,
					ph: 32,
				},
				{
					id: "reel",
					label: "Reality Moment",
					sublabel: "1080×1920 · 9:16",
					hint: "Best for Reels, Stories, and TikTok",
					pw: 24,
					ph: 42.5,
				},
				{
					id: "horizontal",
					label: "Quick Take",
					sublabel: "1200×630 · 16:9",
					hint: "Best for link previews and messaging threads",
					pw: 40,
					ph: 22.5,
				},
			],
			defaultFormat: "portrait",
			shareModalTone: "playful",
			templateOrdering: "narrator-first",
			showFormatHint: true,
			showPreview: true,
			shareFooter: "DramaDetector by NarrativeClarity",
		},
		"habit-engine": {
			streakRewardThreshold: 7,
			questUnlockAnalysisCount: 5,
			celebrationType: "confetti",
			streakResetGracePeriodHours: 36,
			dailyQuestCount: 3,
		},
		dramacards: {
			cardsPerSession: 10,
			deckUnlockThreshold: 100,
			showDifficultyBadge: true,
			sessionCooldownMinutes: 30,
			maxDailySessionsFree: 3,
			maxDailySessionsPremium: 10,
		},
	};
